/**
 * Platform Dashboard
 *
 * Landing page for Platform Administrators.
 * Provides system-wide overview and quick access to admin functions.
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import {
  getPendingUsers,
  getAllUsers,
  getAllOrganizations,
  getRecentAuditLogs,
  type UserProfile,
  type Organization,
  type AuditLog,
} from '../../services/firestore';
import { getRatedInstitutions } from '../../services/institutionService';
import './DashboardStyles.css';

interface DashboardStats {
  organizations: number;
  cras: number;
  banks: number;
  institutions: number;
  users: number;
  pendingApprovals: number;
}

export default function PlatformDashboard() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { userProfile } = useAuth();
  const [stats, setStats] = useState<DashboardStats>({
    organizations: 0,
    cras: 0,
    banks: 0,
    institutions: 0,
    users: 0,
    pendingApprovals: 0,
  });
  const [recentActivity, setRecentActivity] = useState<AuditLog[]>([]);
  const [pendingUsers, setPendingUsers] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(true);

  const firstName = userProfile?.display_name?.split(' ')[0] || 'Admin';

  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        setLoading(true);

        // Fetch all data in parallel
        const [users, orgs, pending, logs, institutions] = await Promise.all([
          getAllUsers(),
          getAllOrganizations(),
          getPendingUsers(),
          getRecentAuditLogs(10),
          getRatedInstitutions(),
        ]);

        // Calculate stats
        const cras = orgs.filter((o: Organization) => o.type === 'cra').length;
        const banks = orgs.filter((o: Organization) => o.type === 'bank').length;
        const institutionCount = institutions.length;

        setStats({
          organizations: orgs.length,
          cras,
          banks,
          institutions: institutionCount,
          users: users.length,
          pendingApprovals: pending.length,
        });

        setPendingUsers(pending.slice(0, 5));
        setRecentActivity(logs);
      } catch (error) {
        console.error('Error loading dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, []);

  const formatTimeAgo = (timestamp: any): string => {
    if (!timestamp) return t('dashboard.unknown');
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return t('dashboard.justNow');
    if (diffMins < 60) return t('time.minutesAgo', { count: diffMins });
    if (diffHours < 24) return t('time.hoursAgo', { count: diffHours });
    return t('time.daysAgo', { count: diffDays });
  };

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1>{t('dashboard.welcomeBack', { name: firstName })}</h1>
        <p className="dashboard-subtitle">{t('dashboard.platformSubtitle')}</p>
      </div>

      <div className="dashboard-content">
        {/* Quick Actions */}
        <div className="dashboard-section">
          <h2 className="section-title">{t('dashboard.quickActions')}</h2>
          <div className="actions-grid">
            <button className="action-card primary" onClick={() => navigate('/admin')}>
              <span className="action-label">{t('dashboard.adminPanel')}</span>
            </button>
            <button className="action-card" onClick={() => navigate('/admin/users/pending')}>
              <span className="action-label">{t('dashboard.approveUsers')}</span>
            </button>
            <button className="action-card" onClick={() => navigate('/admin/organizations')}>
              <span className="action-label">{t('dashboard.organizations')}</span>
            </button>
            <button className="action-card" onClick={() => navigate('/ratings')}>
              <span className="action-label">{t('dashboard.viewRatings')}</span>
            </button>
            <button className="action-card" onClick={() => navigate('/reports')}>
              <span className="action-label">{t('dashboard.reports')}</span>
            </button>
            <button className="action-card" onClick={() => navigate('/analyst-bench')}>
              <span className="action-label">{t('dashboard.analystBench')}</span>
            </button>
          </div>
        </div>

        {/* System Overview Stats */}
        <div className="dashboard-section">
          <h2 className="section-title">{t('dashboard.systemOverview')}</h2>
          <div className="stats-grid">
            <div className="stat-card" onClick={() => navigate('/admin/organizations')} style={{ cursor: 'pointer' }}>
              <div className="stat-value">{loading ? '...' : stats.organizations}</div>
              <div className="stat-label">{t('dashboard.statOrganizations')}</div>
              <div className="stat-sublabel">
                {!loading && t('dashboard.statCrasAndBanks', { cras: stats.cras, banks: stats.banks })}
              </div>
            </div>
            <div className="stat-card" onClick={() => navigate('/ratings')} style={{ cursor: 'pointer' }}>
              <div className="stat-value">{loading ? '...' : stats.institutions}</div>
              <div className="stat-label">{t('dashboard.statInstitutions')}</div>
            </div>
            <div className="stat-card" onClick={() => navigate('/admin/users')} style={{ cursor: 'pointer' }}>
              <div className="stat-value">{loading ? '...' : stats.users}</div>
              <div className="stat-label">{t('dashboard.statUsers')}</div>
            </div>
            <div
              className={`stat-card ${stats.pendingApprovals > 0 ? 'highlight' : ''}`}
              onClick={() => navigate('/admin/users/pending')}
              style={{ cursor: 'pointer' }}
            >
              <div className="stat-value">{loading ? '...' : stats.pendingApprovals}</div>
              <div className="stat-label">{t('dashboard.pendingApprovals')}</div>
            </div>
          </div>
        </div>

        {/* Pending Approvals Section */}
        {pendingUsers.length > 0 && (
          <div className="dashboard-section">
            <div className="section-header">
              <h2 className="section-title">{t('dashboard.pendingUserApprovals')}</h2>
              <button
                className="section-action"
                onClick={() => navigate('/admin/users/pending')}
              >
                {t('common.viewAll')}
              </button>
            </div>
            <div className="pending-list">
              {pendingUsers.map((user) => (
                <div key={user.uid} className="pending-item">
                  <div className="pending-avatar">
                    {user.display_name?.[0]?.toUpperCase() || '?'}
                  </div>
                  <div className="pending-info">
                    <div className="pending-name">{user.display_name || user.email}</div>
                    <div className="pending-email">{user.email}</div>
                  </div>
                  <div className="pending-time">
                    {formatTimeAgo(user.created_at)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recent Activity */}
        {recentActivity.length > 0 && (
          <div className="dashboard-section">
            <h2 className="section-title">{t('dashboard.recentActivity')}</h2>
            <div className="activity-list">
              {recentActivity.map((log) => (
                <div key={log.id} className="activity-item">
                  <div className="activity-info">
                    <div className="activity-action">{log.action}</div>
                    <div className="activity-target">
                      {log.target_type}: {log.target_id}
                    </div>
                  </div>
                  <div className="activity-time">
                    {formatTimeAgo(log.timestamp)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
